import React, { Component } from "react";
import { message, Space, Form as FormInitial, Row, Col, Tag, Radio } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import {
  ModalItem, ButtonItem, Form, FormItem, InputItem, ErrorItem,
} from "../../../../Components/Items";
import { DownloadOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

/**
 * Vista previa y envío por correo del recibo de nómina de una línea.
 *
 * El PDF lo arma el backend (Pdf/reciboNominaPdf.js), único generador del recibo:
 * lo que se ve aquí es byte por byte el archivo que se adjunta al correo y el que
 * se baja en el ZIP. Funciona igual con líneas sin timbrar — esas salen con marca
 * de agua "VISTA PREVIA - SIN TIMBRAR" y sin datos de CFDI ni QR.
 */
class ModalReciboNomina extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    pdfUrl: null,
    datosRecibo: null,
    // Un finiquito con pagos por separación tiene DOS recibos, uno por CFDI (ver
    // CfdiNomina.division): `divisiones` son los que tiene esta línea y `division` el que se está
    // viendo. Una línea normal trae [null] y el selector no aparece.
    divisiones: [null],
    division: null,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible !== prevProps.visible && this.props.visible) {
      this.cargarRecibo();
    }
    if (this.props.visible !== prevProps.visible && !this.props.visible) {
      this.limpiarPdf();
      this.setState({ datosRecibo: null, divisiones: [null], division: null });
    }
  }

  componentWillUnmount() {
    this.limpiarPdf();
  }

  // Las URLs de object hay que revocarlas a mano o el blob se queda en memoria
  // mientras viva la pestaña.
  limpiarPdf() {
    if (this.state.pdfUrl) URL.revokeObjectURL(this.state.pdfUrl);
    this.setState({ pdfUrl: null });
  }

  async cargarRecibo() {
    this.setState({ loading: true });
    try {
      // 1. Metadata: el correo del empleado para precargar y la bandera de timbrado.
      const resDatos = await axios.post(
        "prenomina/reciboNominaLinea",
        { lineaId: this.props.lineaId, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (resDatos.data.success !== 2) {
        message.error(resDatos.data.message || "Error al cargar el recibo");
        this.setState({ loading: false });
        return;
      }
      if (resDatos.data.empleado?.correo && this.formRef.current) {
        this.formRef.current.setFieldsValue({
          correosEnvio: [{ correo: resDatos.data.empleado.correo }],
        });
      }

      // Los recibos que tiene la línea. Con pagos por separación son dos y hay que poder ver los
      // dos; el primero es el ordinario (o el único).
      const divisiones = resDatos.data.divisiones?.length ? resDatos.data.divisiones : [null];
      const division = divisiones.includes(this.state.division)
        ? this.state.division
        : divisiones[0];

      // 2. El PDF ya renderizado por el backend, el de ESTA división.
      const resPdf = await axios.post(
        "prenomina/verPdfReciboNomina",
        { lineaId: this.props.lineaId, empresaId: this.props.empresaId, division },
        { headers: this.props.headersToken, responseType: "arraybuffer" }
      );
      // Ante un error el endpoint responde JSON, no bytes de PDF. Sin este guard
      // el iframe mostraría el JSON crudo como si fuera el recibo.
      if (!(resPdf.headers["content-type"] || "").includes("application/pdf")) {
        message.error("Error al generar el recibo");
        this.setState({ loading: false });
        return;
      }
      const blob = new Blob([resPdf.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(blob);

      this.setState({ loading: false, pdfUrl, datosRecibo: resDatos.data, divisiones, division });
    } catch {
      message.error("Error al cargar el recibo");
      this.setState({ loading: false });
    }
  }

  /** Cambiar entre el recibo ordinario y el de separación: sólo se vuelve a pedir el PDF. */
  async cambiarDivision(division) {
    if (division === this.state.division) return;
    this.limpiarPdf();
    this.setState({ division, loading: true });
    try {
      const resPdf = await axios.post(
        "prenomina/verPdfReciboNomina",
        { lineaId: this.props.lineaId, empresaId: this.props.empresaId, division },
        { headers: this.props.headersToken, responseType: "arraybuffer" }
      );
      if (!(resPdf.headers["content-type"] || "").includes("application/pdf")) {
        message.error("Error al generar el recibo");
        this.setState({ loading: false });
        return;
      }
      const blob = new Blob([resPdf.data], { type: "application/pdf" });
      this.setState({ loading: false, pdfUrl: URL.createObjectURL(blob) });
    } catch {
      message.error("Error al cargar el recibo");
      this.setState({ loading: false });
    }
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  async onFinish(values) {
    if (!values?.correosEnvio?.length) {
      message.info("Proporciona al menos un correo de envío");
      return;
    }
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "prenomina/enviarReciboNomina",
        { ...values, lineaId: this.props.lineaId, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      this.setState({ loading: false });
      if (res.data.success === 2) {
        message.success("Recibo enviado correctamente");
        this.cerrar();
      } else {
        message.error(res.data.message || "No se pudo enviar el recibo");
      }
    } catch {
      this.setState({ loading: false });
      message.error("Error al enviar el recibo");
    }
  }

  /**
   * El nombre —sin extensión— del recibo que se está viendo, tal como lo bautizó el backend.
   *
   * Se pide en vez de armarlo aquí: es el mismo que llevan el PDF y el XML del ZIP y el adjunto
   * del correo, y mientras se armaba por separado se iba de lado (aquí decía `Recibo_Nomina_` y
   * en el ZIP `Recibo_`). Los dos recibos de un finiquito comparten empleado y periodo, así que
   * el sufijo de división es lo que evita que el segundo pise al primero en las descargas.
   */
  nombreBaseRecibo() {
    const { datosRecibo, division } = this.state;
    const encontrado = datosRecibo?.nombresPorDivision?.find(
      (n) => (n.division ?? null) === (division ?? null)
    );
    return encontrado?.nombreBase || null;
  }

  descargarPdf() {
    const { pdfUrl, datosRecibo } = this.state;
    if (!pdfUrl) return;
    // Respaldo por si la respuesta no trajera los nombres (recibo viejo en caché).
    const respaldo = `Recibo_${datosRecibo?.empleado?.nombre || "recibo"}`
      + `_${datosRecibo?.prenomina?.nombre || ""}${datosRecibo?.timbrada ? "" : "_SIN_TIMBRAR"}`;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${this.nombreBaseRecibo() || respaldo}.pdf`.replace(/[/\\?*[\]:#]/g, "_");
    a.click();
  }

  /**
   * El o los XML timbrados de la línea.
   *
   * Baja los DOS cuando el recibo se partió (ordinario + régimen 13): son dos comprobantes
   * distintos ante el SAT y el empleado necesita ambos, así que no se baja "el de la división
   * que se está viendo" sino todos. El nombre lo da el backend, el mismo del PDF: lleva el
   * sufijo de división y por eso los dos no se pisan.
   */
  async descargarXml() {
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "prenomina/xmlCfdiLinea",
        { lineaId: this.props.lineaId, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      this.setState({ loading: false });
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se encontró el XML de este recibo");
        return;
      }
      // `cfdis` es lo que devuelven los timbrados nuevos; los campos sueltos son el respaldo
      // para un recibo de antes de que un finiquito pudiera emitir dos comprobantes.
      const comprobantes = res.data.cfdis?.length
        ? res.data.cfdis
        : [{ uuid: res.data.uuid, xml: res.data.xml }];
      const conXml = comprobantes.filter((c) => c.xml);
      if (!conXml.length) {
        message.error("Este recibo no tiene XML guardado");
        return;
      }
      for (const c of conXml) {
        const blob = new Blob([c.xml], { type: "application/xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const base = c.nombreBase
          || `${c.uuid}${c.division === "separacion" ? "_SEPARACION" : ""}`;
        a.download = `${base}.xml`.replace(/[/\\?*[\]:#]/g, "_");
        a.click();
        URL.revokeObjectURL(url);
      }
      if (conXml.length > 1) {
        message.success(`Se descargaron los ${conXml.length} XML del recibo (ordinario y de separación)`);
      }
    } catch {
      this.setState({ loading: false });
      message.error("Error al descargar el XML");
    }
  }

  cerrar() {
    this.props.onClose();
    if (this.formRef.current) this.formRef.current.resetFields();
  }

  render() {
    const { visible } = this.props;
    const { loading, pdfUrl, datosRecibo, divisiones, division } = this.state;
    const sinTimbrar = datosRecibo && !datosRecibo.timbrada;
    // Con pagos por separación la línea tiene dos recibos y hay que poder ver los dos.
    const dosRecibos = (divisiones || []).length > 1;

    return (
      <ModalItem
        title={(
          <span>
            {division === "separacion" ? "Recibo de Indemnización o Separación" : "Recibo de Nómina"}
            {sinTimbrar && (
              <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>
                Sin timbrar
              </Tag>
            )}
          </span>
        )}
        visible={visible}
        spinning={loading}
        onCancel={this.cerrar.bind(this)}
        paddingTop
        width={900}
      >
        <Form
          ref={this.formRef}
          name="enviarReciboNomina"
          layout
          onCancel={this.cerrar.bind(this)}
          onFinish={this.onFinish.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          {dosRecibos && (
            <div style={{
              background: "#FFFBE6", border: "1px solid #FFE58F", borderRadius: 6,
              padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#7C5A00",
            }}>
              <div style={{ marginBottom: 6 }}>
                Este finiquito se timbró en <b>dos CFDI</b> —el SAT no permite mezclar los pagos por
                separación con los ordinarios—, así que hay <b>dos recibos</b>. Elige cuál ver o
                descargar; el correo manda los dos juntos.
              </div>
              <Radio.Group
                size="small"
                value={division}
                onChange={(e) => this.cambiarDivision(e.target.value)}
              >
                {divisiones.map((d) => (
                  <Radio.Button key={d || "unico"} value={d}>
                    {d === "separacion" ? "Indemnización o separación (régimen 13)" : "Recibo ordinario"}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          )}

          {sinTimbrar && (
            <div style={{
              background: "#FFF7E6", border: "1px solid #FFD591", borderRadius: 6,
              padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#874D00",
            }}>
              Este recibo todavía no se ha timbrado. El PDF sale marcado como vista
              previa y sin datos de CFDI; si lo mandas, el correo se lo advierte al empleado.
            </div>
          )}

          <FormInitial.List name="correosEnvio" initialValue={[{ correo: "" }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ name }, arrayKey) => (
                  <Row key={arrayKey}>
                    <Col span={22}>
                      <FormItem
                        name={[name, "correo"]}
                        fieldKey={[arrayKey, "correo"]}
                        label="Correo"
                        required
                        margin
                        email
                        pattern
                      >
                        <InputItem style={{ width: "100%" }} placeholder="Correo electrónico" />
                      </FormItem>
                    </Col>
                    <Col span={2}>
                      <div style={{ textAlign: "center", paddingTop: 6 }}>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </div>
                    </Col>
                  </Row>
                ))}
                {fields.length < 5 && (
                  <div style={{ textAlign: "center", marginTop: 5 }}>
                    <ButtonItem icon={<PlusOutlined />} onClick={() => add()}>
                      Agregar correo
                    </ButtonItem>
                  </div>
                )}
              </>
            )}
          </FormInitial.List>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, marginBottom: 10 }}>
            <Space>
              <ButtonItem icon={<DownloadOutlined />} blanco onClick={this.descargarPdf.bind(this)}>
                Descargar PDF
              </ButtonItem>
              {/* El XML sólo existe si hay timbre. En una vista previa el botón no aparece en vez
                  de aparecer y fallar. */}
              {!sinTimbrar && datosRecibo && (
                <ButtonItem icon={<DownloadOutlined />} blanco onClick={this.descargarXml.bind(this)}>
                  Descargar XML
                </ButtonItem>
              )}
              <ButtonItem type="primary" htmlType="submit">
                Mandar Correo
              </ButtonItem>
              <ButtonItem type="primary" danger onClick={this.cerrar.bind(this)}>
                Cancelar
              </ButtonItem>
            </Space>
          </div>
        </Form>

        <iframe
          title="recibo-nomina"
          style={{ width: "100%", height: "75vh" }}
          id="nominaReciboPdf"
          name="nominaReciboPdf"
          src={pdfUrl || undefined}
        />
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalReciboNomina);
